import type { CSSProperties } from "react";
import { siteBuilderFieldValue } from "./siteBuilderFieldValues";
import { siteBuilderInteractionStyle } from "./siteBuilderInteractions";
import { siteBuilderPartOptionKey } from "./siteBuilderPartOptions";
import type { SiteBuilderBlock } from "./siteBuilderTypes";

export { siteBuilderPartOptionKey } from "./siteBuilderPartOptions";

export type SiteBuilderStyleRecord = CSSProperties
    & Record<string, string | number | undefined>;

const SITE_BUILDER_AUTHORITATIVE_STYLE_PROPERTIES = [
    ["alignContent", "align-content"],
    ["alignItems", "align-items"],
    ["backdropFilter", "backdrop-filter"],
    ["backgroundColor", "background-color"],
    ["backgroundImage", "background-image"],
    ["backgroundPosition", "background-position"],
    ["backgroundRepeat", "background-repeat"],
    ["backgroundSize", "background-size"],
    ["borderColor", "border-color"],
    ["borderRadius", "border-radius"],
    ["borderStyle", "border-style"],
    ["borderWidth", "border-width"],
    ["boxShadow", "box-shadow"],
    ["color", "color"],
    ["fontFamily", "font-family"],
    ["fontSize", "font-size"],
    ["fontStyle", "font-style"],
    ["fontWeight", "font-weight"],
    ["gap", "gap"],
    ["height", "height"],
    ["letterSpacing", "letter-spacing"],
    ["lineHeight", "line-height"],
    ["marginBottom", "margin-bottom"],
    ["marginLeft", "margin-left"],
    ["marginRight", "margin-right"],
    ["marginTop", "margin-top"],
    ["maxWidth", "max-width"],
    ["minHeight", "min-height"],
    ["opacity", "opacity"],
    ["overflow", "overflow"],
    ["padding", "padding"],
    ["paddingBottom", "padding-bottom"],
    ["paddingLeft", "padding-left"],
    ["paddingRight", "padding-right"],
    ["paddingTop", "padding-top"],
    ["position", "position"],
    ["textAlign", "text-align"],
    ["textDecoration", "text-decoration"],
    ["textTransform", "text-transform"],
    ["top", "top"],
    ["transform", "transform"],
    ["whiteSpace", "white-space"],
    ["width", "width"],
    ["zIndex", "z-index"],
] as const;

/**
 * Template skins are allowed to provide strong defaults, but an explicit
 * builder value is authoritative in both the editor and the public runtime.
 * The marker lives on the same element as the regular inline declaration so
 * a shared rule can safely beat legacy template selectors using !important.
 */
export const SITE_BUILDER_AUTHORITATIVE_STYLE_CSS =
    SITE_BUILDER_AUTHORITATIVE_STYLE_PROPERTIES.map(([, cssProperty]) => {
        const variable = `--site-builder-authored-${cssProperty}`;

        // `:is()` adopts the strongest specificity of its selector list. The
        // deliberately absent id gives an authored value one id of
        // specificity while the attribute branch is the one that actually
        // matches. This lets persisted builder values beat legacy template
        // skins that use highly specific `!important` declarations.
        const authoredValueSelector = `:is(#__site-builder-authored-style-specificity, [style*="${variable}:"])`;

        return `${authoredValueSelector} { ${cssProperty}: var(${variable}) !important; }`;
    }).join("\n");

function siteBuilderAuthoritativeStyleVariables(
    style: SiteBuilderStyleRecord,
): SiteBuilderStyleRecord {
    return Object.fromEntries(
        SITE_BUILDER_AUTHORITATIVE_STYLE_PROPERTIES.flatMap(
            ([reactProperty, cssProperty]) => {
                const value = style[reactProperty];

                return value === undefined || value === null
                    ? []
                    : [[`--site-builder-authored-${cssProperty}`, String(value)]];
            },
        ),
    );
}

/**
 * Keeps the authoritative markers emitted by the neutral resolver while
 * rebasing their values onto a template's final, normalized declarations.
 * A template may translate portable tokens (for example `lg`) or legacy
 * colors before rendering; the marker must carry that translated value too,
 * otherwise the shared `!important` rule would restore the raw input.
 *
 * Markers are never inferred from template defaults. When a normalized
 * declaration was deliberately removed, `undefined` clears the stale marker
 * when this result is spread over the authored style.
 */
export function siteBuilderRebasedAuthoritativeStyleVariables(
    authoredStyle: SiteBuilderStyleRecord,
    normalizedStyle: SiteBuilderStyleRecord,
): SiteBuilderStyleRecord {
    return Object.fromEntries(
        SITE_BUILDER_AUTHORITATIVE_STYLE_PROPERTIES.flatMap(
            ([reactProperty, cssProperty]) => {
                const variable = `--site-builder-authored-${cssProperty}`;

                if (authoredStyle[variable] === undefined || authoredStyle[variable] === null) {
                    return [];
                }

                const value = normalizedStyle[reactProperty];

                return [[
                    variable,
                    value === undefined || value === null ? undefined : String(value),
                ]];
            },
        ),
    );
}

/**
 * Visual fields that the renderer can safely project onto an editable
 * sub-part (title, image, icon, button label, ...). The editor consumes this
 * same contract so it cannot expose a part control that rendering ignores.
 */
export const SITE_BUILDER_PART_STYLE_KEYS = [
    "animation",
    "animationDirection",
    "animationDuration",
    "animationEffect",
    "animationMode",
    "animationScrollEnd",
    "animationScrollStart",
    "animationStartAfter",
    "animationTrigger",
    "backdropBlur",
    "backdropFilter",
    "backgroundColor",
    "backgroundImage",
    "backgroundOverlay",
    "backgroundPosition",
    "backgroundSize",
    "borderColor",
    "borderRadius",
    "borderStyle",
    "borderWidth",
    "boxShadow",
    "fontFamily",
    "fontSize",
    "fontStyle",
    "fontWeight",
    "gap",
    "height",
    "hoverBackgroundColor",
    "hoverBorderColor",
    "hoverDuration",
    "hoverEffect",
    "hoverTextColor",
    "letterSpacing",
    "lineHeight",
    "marginBottom",
    "marginLeft",
    "marginRight",
    "marginTop",
    "maxWidth",
    "minHeight",
    "opacity",
    "overflow",
    "padding",
    "paddingBottom",
    "paddingLeft",
    "paddingRight",
    "paddingTop",
    "position",
    "textAlign",
    "textColor",
    "textDecoration",
    "textTransform",
    "top",
    "transform",
    "verticalAlign",
    "whiteSpace",
    "width",
    "zIndex",
] as const;

function record(value: unknown): Record<string, unknown> {
    return value && typeof value === "object" && !Array.isArray(value)
        ? value as Record<string, unknown>
        : {};
}

function textValue(value: unknown, fallback = ""): string {
    return typeof value === "string" ? value : fallback;
}

function numberValue(value: unknown, fallback = 0): number {
    const numeric = typeof value === "number" ? value : Number(value);

    return Number.isFinite(numeric) ? numeric : fallback;
}

function option(block: SiteBuilderBlock, key: string): unknown {
    return block.options?.[key];
}

function optionText(
    block: SiteBuilderBlock,
    key: string,
    fallback = "",
): string {
    return textValue(option(block, key), fallback);
}

function contentText(block: SiteBuilderBlock, key: string): string {
    return textValue(block.content?.[key]);
}

function siteBuilderBackgroundImageValue(value: string): string {
    const trimmed = value.trim();

    return /(?:gradient|image-set)\(/iu.test(trimmed)
        || /^url\(/iu.test(trimmed)
        ? trimmed
        : `url(${trimmed})`;
}

function siteBuilderContentColorVariables(
    textColor: string,
): SiteBuilderStyleRecord {
    const trimmed = textColor.trim();

    if (trimmed === "" || trimmed === "var(--site-text)") {
        return {};
    }

    return {
        "--site-text": trimmed,
        "--site-muted": `color-mix(in srgb, ${trimmed} 68%, transparent)`,
    };
}

function siteBuilderBoxShadowValue(value: string): string {
    const presets: Record<string, string> = {
        glow: "0 0 0 1px color-mix(in srgb, var(--site-primary) 24%, transparent), 0 16px 44px color-mix(in srgb, var(--site-primary) 22%, transparent)",
        medium: "0 16px 38px var(--site-shadow-color, rgba(15, 23, 42, .18))",
        none: "none",
        soft: "0 10px 28px color-mix(in srgb, var(--site-shadow-color, rgba(15, 23, 42, .14)) 72%, transparent)",
        strong: "0 24px 64px color-mix(in srgb, var(--site-shadow-color, rgba(15, 23, 42, .28)) 88%, var(--site-text, #0f172a))",
    };

    return presets[value] ?? value;
}

export function siteBuilderCssLength(value: unknown): string | undefined {
    if (typeof value === "number") {
        return `${value}px`;
    }

    if (typeof value !== "string" || value.trim() === "") {
        return undefined;
    }

    return /^\d+(\.\d+)?$/u.test(value.trim())
        ? `${value.trim()}px`
        : value.trim();
}

/**
 * The canonical visual option resolver used by every Site Builder host.
 *
 * Module adapters still own defaults and labels. Rendering the resulting
 * options is deliberately neutral so a Shop and a Web block with the same
 * options cannot drift visually.
 */
export function siteBuilderBlockStyle(
    block: SiteBuilderBlock,
): SiteBuilderStyleRecord {
    const storedStyle = record(option(block, "style"));
    const backgroundImage = optionText(block, "backgroundImage")
        || contentText(block, "backgroundImage");
    const backgroundColor = optionText(block, "backgroundColor")
        || textValue(storedStyle.backgroundColor);
    const textColor = optionText(block, "textColor")
        || textValue(storedStyle.color);
    const borderColor = optionText(block, "borderColor")
        || textValue(storedStyle.borderColor);
    const borderWidth = siteBuilderCssLength(
        option(block, "borderWidth") ?? storedStyle.borderWidth,
    );
    const borderRadius = siteBuilderCssLength(
        option(block, "borderRadius") ?? storedStyle.borderRadius,
    );
    const minHeight = siteBuilderCssLength(
        option(block, "minHeight") ?? storedStyle.minHeight,
    );
    const maxWidth = siteBuilderCssLength(
        option(block, "maxWidth") ?? storedStyle.maxWidth,
    );
    const width = siteBuilderCssLength(
        option(block, "width") ?? storedStyle.width,
    );
    const height = siteBuilderCssLength(
        option(block, "height") ?? storedStyle.height,
    );
    const padding = siteBuilderCssLength(
        option(block, "padding") ?? storedStyle.padding,
    );
    const paddingTop = siteBuilderCssLength(
        option(block, "paddingTop") ?? storedStyle.paddingTop,
    );
    const paddingRight = siteBuilderCssLength(
        option(block, "paddingRight") ?? storedStyle.paddingRight,
    );
    const paddingBottom = siteBuilderCssLength(
        option(block, "paddingBottom") ?? storedStyle.paddingBottom,
    );
    const paddingLeft = siteBuilderCssLength(
        option(block, "paddingLeft") ?? storedStyle.paddingLeft,
    );
    const marginTop = siteBuilderCssLength(
        option(block, "marginTop") ?? storedStyle.marginTop,
    );
    const marginRight = siteBuilderCssLength(
        option(block, "marginRight") ?? storedStyle.marginRight,
    );
    const marginBottom = siteBuilderCssLength(
        option(block, "marginBottom") ?? storedStyle.marginBottom,
    );
    const marginLeft = siteBuilderCssLength(
        option(block, "marginLeft") ?? storedStyle.marginLeft,
    );
    const gap = siteBuilderCssLength(
        option(block, "gap") ?? storedStyle.gap,
    );
    const opacity = numberValue(
        option(block, "opacity") ?? storedStyle.opacity,
        1,
    );
    const textAlign = optionText(block, "textAlign")
        || optionText(block, "alignment")
        || textValue(storedStyle.textAlign);
    const fontSize = siteBuilderCssLength(
        option(block, "fontSize") ?? storedStyle.fontSize,
    );
    const fontWeight = option(block, "fontWeight")
        ?? storedStyle.fontWeight;
    const fontStyle = optionText(block, "fontStyle")
        || textValue(storedStyle.fontStyle);
    const lineHeight = option(block, "lineHeight")
        ?? storedStyle.lineHeight;
    const fontFamily = optionText(block, "fontFamily")
        || textValue(storedStyle.fontFamily);
    const letterSpacing = siteBuilderCssLength(
        option(block, "letterSpacing") ?? storedStyle.letterSpacing,
    );
    const textDecoration = optionText(block, "textDecoration")
        || textValue(storedStyle.textDecoration);
    const textTransform = optionText(block, "textTransform")
        || textValue(storedStyle.textTransform);
    const whiteSpace = optionText(block, "whiteSpace")
        || textValue(storedStyle.whiteSpace);
    const verticalAlign = optionText(block, "verticalAlign");
    const position = optionText(block, "position")
        || textValue(storedStyle.position);
    const top = siteBuilderCssLength(
        option(block, "top") ?? storedStyle.top,
    );
    const transform = optionText(block, "transform")
        || textValue(storedStyle.transform);
    const zIndex = option(block, "zIndex") ?? storedStyle.zIndex;
    const backdropBlur = siteBuilderCssLength(
        siteBuilderFieldValue(block, "options", "backdropBlur"),
    );
    const backdropFilter = optionText(block, "backdropFilter")
        || textValue(storedStyle.backdropFilter);
    const boxShadow = optionText(block, "boxShadow")
        || textValue(storedStyle.boxShadow);
    const overflow = optionText(block, "overflow")
        || textValue(storedStyle.overflow);
    const borderStyle = optionText(block, "borderStyle")
        || textValue(storedStyle.borderStyle);
    const overlay = optionText(
        block,
        "backgroundOverlay",
        "rgba(0,0,0,0)",
    );
    const overlayImage = overlay.includes("gradient(")
        ? overlay
        : `linear-gradient(${overlay}, ${overlay})`;

    const resolvedStyle: SiteBuilderStyleRecord = {
        ...(backgroundColor ? { backgroundColor } : {}),
        ...(backgroundImage ? {
            backgroundImage: `${overlayImage}, ${siteBuilderBackgroundImageValue(backgroundImage)}`,
            backgroundPosition: optionText(
                block,
                "backgroundPosition",
                "center",
            ),
            backgroundRepeat: "no-repeat",
            backgroundSize: optionText(block, "backgroundSize", "cover"),
        } : {}),
        ...(textColor ? {
            color: textColor,
            ...siteBuilderContentColorVariables(textColor),
        } : {}),
        ...(borderColor ? { borderColor } : {}),
        ...(borderStyle === "solid"
            || borderStyle === "dashed"
            || borderStyle === "dotted"
            || borderStyle === "double"
            || borderStyle === "none"
            ? { borderStyle }
            : borderColor
                ? { borderStyle: "solid" }
                : {}),
        ...(borderWidth ? { borderWidth } : {}),
        ...(borderRadius ? { borderRadius } : {}),
        ...(minHeight ? { minHeight } : {}),
        ...(maxWidth ? { maxWidth } : {}),
        ...(width ? { width } : {}),
        ...(height ? { height } : {}),
        ...(padding ? { padding } : {}),
        ...(paddingTop ? { paddingTop } : {}),
        ...(paddingRight ? { paddingRight } : {}),
        ...(paddingBottom ? { paddingBottom } : {}),
        ...(paddingLeft ? { paddingLeft } : {}),
        ...(marginTop ? { marginTop } : {}),
        ...(marginRight ? { marginRight } : {}),
        ...(marginBottom ? { marginBottom } : {}),
        ...(marginLeft ? { marginLeft } : {}),
        ...(gap ? { gap } : {}),
        ...(opacity !== 1 ? { opacity } : {}),
        ...(textAlign === "center"
            || textAlign === "right"
            || textAlign === "left"
            ? { textAlign }
            : {}),
        ...(fontSize ? { fontSize } : {}),
        ...(typeof fontWeight === "string"
            || typeof fontWeight === "number"
            ? { fontWeight }
            : {}),
        ...(fontStyle === "normal" || fontStyle === "italic"
            ? { fontStyle }
            : {}),
        ...(typeof lineHeight === "string"
            || typeof lineHeight === "number"
            ? { lineHeight }
            : {}),
        ...(fontFamily ? { fontFamily } : {}),
        ...(letterSpacing ? { letterSpacing } : {}),
        ...(textDecoration === "none"
            || textDecoration === "underline"
            || textDecoration === "line-through"
            ? { textDecoration }
            : {}),
        ...(textTransform === "uppercase"
            || textTransform === "lowercase"
            || textTransform === "capitalize"
            || textTransform === "none"
            ? { textTransform }
            : {}),
        ...(whiteSpace === "pre-line"
            || whiteSpace === "pre-wrap"
            || whiteSpace === "normal"
            || whiteSpace === "nowrap"
            ? { whiteSpace }
            : {}),
        ...(verticalAlign === "center"
            ? { alignContent: "center", alignItems: "center" }
            : verticalAlign === "end" || verticalAlign === "bottom"
                ? { alignContent: "end", alignItems: "end" }
                : verticalAlign === "start" || verticalAlign === "top"
                    ? { alignContent: "start", alignItems: "start" }
                    : {}),
        ...(position === "relative"
            || position === "absolute"
            || position === "fixed"
            || position === "sticky"
            ? { position }
            : {}),
        ...(top ? { top } : {}),
        ...(transform ? { transform } : {}),
        ...(typeof zIndex === "string" || typeof zIndex === "number"
            ? { zIndex }
            : {}),
        ...(boxShadow ? { boxShadow: siteBuilderBoxShadowValue(boxShadow) } : {}),
        ...(backdropBlur
            ? { backdropFilter: `blur(${backdropBlur})` }
            : backdropFilter
                ? { backdropFilter }
                : {}),
        ...(overflow === "hidden"
            || overflow === "auto"
            || overflow === "scroll"
            || overflow === "visible"
            ? { overflow }
            : {}),
        ...siteBuilderInteractionStyle(block),
    };

    return {
        ...resolvedStyle,
        ...siteBuilderAuthoritativeStyleVariables(resolvedStyle),
    };
}

/**
 * Resolves the same canonical visual contract for a concrete editable
 * sub-part. Part values are stored as flattened option keys
 * (`titleFontSize`, `primaryButtonBackgroundColor`, ...); projecting them
 * back to root keys lets every renderer reuse the neutral CSS resolver
 * instead of maintaining a second, drifting allow-list.
 */
export function siteBuilderPartStyle(
    block: SiteBuilderBlock,
    part: string,
): SiteBuilderStyleRecord {
    const options = record(block.options);
    const scopedOptions = Object.fromEntries(
        SITE_BUILDER_PART_STYLE_KEYS.flatMap((key) => {
            const scopedKey = siteBuilderPartOptionKey(part, key);

            return Object.prototype.hasOwnProperty.call(options, scopedKey)
                ? [[key, options[scopedKey]]]
                : [];
        }),
    );

    return siteBuilderBlockStyle({
        ...block,
        content: {},
        options: scopedOptions,
    });
}
